import { Outlet, useLocation } from '@remix-run/react'
import type { LoaderFunctionArgs } from '@remix-run/server-runtime'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { requireNoSessionSeller } from '@/app/services/auth-seller.server'
import { SCREEN_MD } from '@/app/utils/constants'
import { Button } from '~/components/ui/Button'
import { Beta } from '~/components/ui/Icons'
import { Tag } from '~/components/ui/Tag/Tag'

import LoginSlider from './components/slider/LoginSlider'

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  await requireNoSessionSeller(loaderArgs.request)
  return null
}

export default function LoginLayout() {
  const { t } = useTranslation()
  let location = useLocation()

  const isInConfirmPage = location.pathname.includes('login/confirm')

  const [isSliderVisible, setSliderVisible] = useState(true)
  const [isSliderButtonVisible, setSliderButtonVisible] = useState(false)
  // initialises the form/outlet visibilty has false: removes the glitch on mobile
  const [isFormVisible, setFormVisible] = useState(false)
  // PS: tried initialising as below but window would always be undefined due to serverside rendering
  //   const [isFormVisible, setFormVisible] = useState(
  //   !(typeof window !== 'undefined' && window.innerWidth < SCREEN_MD)
  //   )

  const [tryButtonClicked, setTryButtonClicked] = useState(false)

  useEffect(() => {
    function updateSize() {
      // If the user is on mobile, the image slider and the login should be in "different pages".
      if (window.innerWidth < SCREEN_MD) {
        // If the user is in the confirm page (aka he already saw the slider when he tried to login) or if he already clicked the "try now for free" button on the slider, the form (login or code) should be shown.
        if (isInConfirmPage || tryButtonClicked) {
          setFormVisible(true)
          setSliderButtonVisible(false)
          setSliderVisible(false)
        } else {
          setFormVisible(false)
          setSliderButtonVisible(true)
          setSliderVisible(true)
        }
      } else {
        setFormVisible(true)
        setSliderButtonVisible(false)
        setSliderVisible(true)
      }
    }
    window.addEventListener('resize', updateSize)
    updateSize()
    return () => window.removeEventListener('resize', updateSize)
  }, [tryButtonClicked, isInConfirmPage])

  return (
    <div className="flex h-full min-h-0 w-full flex-col items-center overflow-auto p-4 xs:p-6">
      <div className="flex h-full w-full gap-6">
        {isSliderVisible && (
          <div className="flex h-full max-h-[95svh] w-full flex-col gap-6 xs:max-h-full md:w-1/2 lg:w-3/5">
            {isSliderButtonVisible && (
              <Tag
                className="w-fit self-end"
                prefixIcon={Beta}
                label={t('page.login.beta_version')}
              />
            )}
            <LoginSlider className="w-full flex-1" />
            {isSliderButtonVisible && (
              <Button
                size="lg"
                className="w-full"
                onClick={() => {
                  setSliderVisible(false)
                  setFormVisible(true)
                  setTryButtonClicked(true)
                }}
              >
                {t('action.try_now')}
              </Button>
            )}
          </div>
        )}
        {isFormVisible && <Outlet />}
      </div>
    </div>
  )
}
